/**
 * Screenshot Utilities
 *
 * Utilities for capturing, compressing, and validating screenshots.
 * Uses html-to-image for viewport capture and Canvas API for WebP compression.
 * Includes aggressive oklch() color preprocessing to handle modern CSS.
 *
 * @module utils/screenshot
 */

import { toCanvas } from 'html-to-image';

/**
 * Modern CSS color functions that html2canvas doesn't support
 * Handles nested parentheses for color-mix() and other complex color functions
 * Also matches gradients and shadows containing these functions
 */
const UNSUPPORTED_COLOR_REGEX = /\b(?:oklch|oklab|lab|lch|color|color-mix|hwb|device-cmyk)\s*\([^)]*(?:\([^)]*\))*[^)]*\)/gi;

/**
 * Pattern to catch color functions in gradients, shadows, and other composite properties
 */
const GRADIENT_SHADOW_REGEX = /(?:linear-gradient|radial-gradient|conic-gradient|repeating-linear-gradient|repeating-radial-gradient|repeating-conic-gradient|(?:text-)?box-shadow)\s*\([^)]*\b(?:oklch|oklab|lab|lch|color|color-mix|hwb|device-cmyk)\s*\([^)]*(?:\([^)]*\))*[^)]*\)[^)]*\)/gi;

/**
 * Apply computed styles as inline styles to ensure html-to-image captures them correctly
 * This is a critical step for capturing calculated styles from external stylesheets
 * IMPORTANT: Replace any unsupported color functions in computed values
 *
 * OPTIMIZED: Only log summary, not individual color replacements (was causing 200+ log messages)
 */
function applyComputedStylesToElements(): void {
  try {
    let elementsProcessed = 0;
    let colorsReplaced = 0;

    // Mapping of oklch values to hex for replacement
    const oklchReplacements: Record<string, string> = {
      'oklch(1 0 0)': '#ffffff',
      'oklch(0.985 0 0)': '#fcfcfc',
      'oklch(0.97 0 0)': '#f7f7f7',
      'oklch(0.922 0 0)': '#ebebeb',
      'oklch(0.708 0 0)': '#b3b3b3',
      'oklch(0.645 0.222 41.116)': '#d97706',
      'oklch(0.6 0.118 184.704)': '#3b82f6',
      'oklch(0.398 0.07 227.392)': '#1e3a8a',
      'oklch(0.828 0.189 84.429)': '#fbbf24',
      'oklch(0.769 0.188 70.08)': '#84cc16',
      'oklch(0.556 0 0)': '#8d8d8d',
      'oklch(0.577 0.245 27.325)': '#d32f2f',
      'oklch(0.205 0 0)': '#353535',
      'oklch(0.145 0 0)': '#252525',
    };

    // Process all elements in the document
    const allElements = document.querySelectorAll('*');
    allElements.forEach((el) => {
      const htmlEl = el as HTMLElement;
      const computedStyle = window.getComputedStyle(htmlEl);

      // Properties that are critical for rendering and might contain unsupported colors
      const criticalProps = [
        'backgroundColor',
        'color',
        'borderColor',
        'borderTopColor',
        'borderRightColor',
        'borderBottomColor',
        'borderLeftColor',
        'boxShadow',
        'textShadow',
        'backgroundImage',
        'outlineColor',
      ];

      let hasChanges = false;
      for (const prop of criticalProps) {
        let value = computedStyle.getPropertyValue(prop);

        if (value && value !== 'initial' && value !== 'inherit') {
          // CRITICAL: Replace any unsupported color functions
          if (UNSUPPORTED_COLOR_REGEX.test(value)) {
            // Try known replacements first
            for (const [oklch, hex] of Object.entries(oklchReplacements)) {
              if (value.includes(oklch)) {
                value = value.replace(new RegExp(oklch, 'g'), hex);
                colorsReplaced++;
              }
            }

            // Replace any remaining with fallback
            if (UNSUPPORTED_COLOR_REGEX.test(value)) {
              value = value.replace(UNSUPPORTED_COLOR_REGEX, '#999999');
              colorsReplaced++;
            }
          }

          const cssProperty = prop
            .replace(/([A-Z])/g, '-$1')
            .toLowerCase();
          if (!htmlEl.style.getPropertyValue(cssProperty)) {
            htmlEl.style.setProperty(cssProperty, value, 'important');
            hasChanges = true;
          }
        }
      }

      if (hasChanges) {
        elementsProcessed++;
      }
    });

    console.log(`[Screenshot] Processed ${elementsProcessed} elements, replaced ${colorsReplaced} oklch colors`);
  } catch (e) {
    console.warn('[Screenshot] Error applying computed styles:', e);
  }
}

/**
 * Pre-process original document to replace oklch colors before html-to-image clones it
 * This is crucial because html-to-image parses colors during cloning
 *
 * OPTIMIZED: Reduced logging to essential messages only
 */
function preProcessOklchInDocument(): void {
  try {
    // Step 0: Apply computed styles as inline to capture all calculated colors
    applyComputedStylesToElements();

    // Step 1: Replace oklch in all text content of style elements
    const styleElements = document.querySelectorAll('style');

    const oklchReplacements: Record<string, string> = {
      'oklch(1 0 0)': '#ffffff',
      'oklch(0.985 0 0)': '#fcfcfc',
      'oklch(0.97 0 0)': '#f7f7f7',
      'oklch(0.922 0 0)': '#ebebeb',
      'oklch(0.708 0 0)': '#b3b3b3',
      'oklch(0.645 0.222 41.116)': '#d97706',
      'oklch(0.6 0.118 184.704)': '#3b82f6',
      'oklch(0.398 0.07 227.392)': '#1e3a8a',
      'oklch(0.828 0.189 84.429)': '#fbbf24',
      'oklch(0.769 0.188 70.08)': '#84cc16',
      'oklch(0.556 0 0)': '#8d8d8d',
      'oklch(0.577 0.245 27.325)': '#d32f2f',
      'oklch(0.205 0 0)': '#353535',
      'oklch(0.145 0 0)': '#252525',
    };

    let totalReplacementsCount = 0;
    let stylesModified = 0;

    styleElements.forEach((style) => {
      if (!style.textContent) return;
      let content = style.textContent;
      const originalContent = content;

      // Replace known oklch values first
      for (const [oklch, hex] of Object.entries(oklchReplacements)) {
        const escapedOklch = oklch.replace(/[()]/g, '\\$&');
        const regex = new RegExp(escapedOklch, 'g');
        const matches = content.match(regex) || [];
        if (matches.length > 0) {
          content = content.replace(regex, hex);
          totalReplacementsCount += matches.length;
        }
      }

      // Multi-pass: Replace any remaining unsupported color functions
      let passNum = 1;
      let remainingMatches = content.match(UNSUPPORTED_COLOR_REGEX) || [];
      while (remainingMatches.length > 0 && passNum <= 5) {
        const previousLength = content.length;
        content = content.replace(UNSUPPORTED_COLOR_REGEX, '#999999');
        totalReplacementsCount += remainingMatches.length;

        if (content.length === previousLength) break;
        passNum++;
        remainingMatches = content.match(UNSUPPORTED_COLOR_REGEX) || [];
      }

      // Handle gradients and shadows containing unsupported colors
      const gradientMatches = content.match(GRADIENT_SHADOW_REGEX) || [];
      if (gradientMatches.length > 0) {
        content = content.replace(GRADIENT_SHADOW_REGEX, '#999999');
        totalReplacementsCount += gradientMatches.length;
      }

      // Replace color functions in filter properties
      const filterRegex = /filter:\s*[^;]*\b(?:oklch|oklab|lab|lch|color|color-mix|hwb|device-cmyk)\s*\([^)]*\)[^;]*/gi;
      const filterMatches = content.match(filterRegex) || [];
      if (filterMatches.length > 0) {
        content = content.replace(filterRegex, 'filter: none');
        totalReplacementsCount += filterMatches.length;
      }

      // Only update if content changed
      if (content !== originalContent) {
        style.textContent = content;
        stylesModified++;
      }
    });

    if (totalReplacementsCount > 0) {
      console.log(`[Screenshot] Preprocessed ${styleElements.length} styles, modified ${stylesModified}, replaced ${totalReplacementsCount} colors`);
    }

    // Step 2: Create override stylesheet with CSS variables
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      :root {
        --background: #ffffff !important;
        --foreground: #252525 !important;
        --card: #ffffff !important;
        --card-foreground: #252525 !important;
        --popover: #ffffff !important;
        --popover-foreground: #252525 !important;
        --primary: #353535 !important;
        --primary-foreground: #fcfcfc !important;
        --secondary: #f7f7f7 !important;
        --secondary-foreground: #353535 !important;
        --muted: #f7f7f7 !important;
        --muted-foreground: #8d8d8d !important;
        --accent: #f7f7f7 !important;
        --accent-foreground: #353535 !important;
        --destructive: #d32f2f !important;
        --border: #ebebeb !important;
        --input: #ebebeb !important;
        --ring: #b3b3b3 !important;
        --chart-1: #d97706 !important;
        --chart-2: #3b82f6 !important;
        --chart-3: #1e3a8a !important;
        --chart-4: #fbbf24 !important;
        --chart-5: #84cc16 !important;
        --sidebar: #fcfcfc !important;
        --sidebar-foreground: #252525 !important;
        --sidebar-primary: #353535 !important;
        --sidebar-primary-foreground: #fcfcfc !important;
        --sidebar-accent: #f7f7f7 !important;
        --sidebar-accent-foreground: #353535 !important;
        --sidebar-border: #ebebeb !important;
        --sidebar-ring: #b3b3b3 !important;
      }

      .dark {
        --background: #252525 !important;
        --foreground: #fcfcfc !important;
        --card: #353535 !important;
        --card-foreground: #fcfcfc !important;
        --popover: #353535 !important;
        --popover-foreground: #fcfcfc !important;
        --primary: #ebebeb !important;
        --primary-foreground: #353535 !important;
        --secondary: #444444 !important;
        --secondary-foreground: #fcfcfc !important;
        --muted: #444444 !important;
        --muted-foreground: #b3b3b3 !important;
        --accent: #444444 !important;
        --accent-foreground: #fcfcfc !important;
        --destructive: #c91c1c !important;
        --border: rgba(255, 255, 255, 0.1) !important;
        --input: rgba(255, 255, 255, 0.15) !important;
        --ring: #8d8d8d !important;
        --chart-1: #7c3aed !important;
        --chart-2: #06b6d4 !important;
        --chart-3: #84cc16 !important;
        --chart-4: #ec4899 !important;
        --chart-5: #f97316 !important;
        --sidebar: #353535 !important;
        --sidebar-foreground: #fcfcfc !important;
        --sidebar-primary: #7c3aed !important;
        --sidebar-primary-foreground: #fcfcfc !important;
        --sidebar-accent: #444444 !important;
        --sidebar-accent-foreground: #fcfcfc !important;
        --sidebar-border: rgba(255, 255, 255, 0.1) !important;
        --sidebar-ring: #8d8d8d !important;
      }
    `;

    // Add to document head immediately
    document.head.appendChild(styleElement);

    // Mark it so we can remove it after screenshot
    styleElement.setAttribute('data-oklch-fix', 'true');

    console.log('[Screenshot] ✓ Injected override stylesheet with CSS variables into original document');
  } catch (e) {
    console.warn('[Screenshot] Error pre-processing oklch colors:', e);
  }
}

/**
 * Clean up the temporary oklch fix stylesheet
 */
function cleanupOklchFix(): void {
  try {
    const fixStyle = document.querySelector('style[data-oklch-fix="true"]');
    if (fixStyle) {
      fixStyle.remove();
    }
  } catch (e) {
    console.warn('[Screenshot] Error cleaning up oklch fix:', e);
  }
}

/**
 * Capture the current viewport as a base64-encoded image
 *
 * Uses html2canvas to capture the entire viewport including scrollable areas.
 * Captures at device pixel ratio for high-DPI displays.
 *
 * @returns Promise resolving to base64-encoded data URL (format: "data:image/png;base64,...")
 * @throws {Error} If capture fails or browser doesn't support required APIs
 *
 * @example
 * ```typescript
 * try {
 *   const screenshot = await captureViewport();
 *   console.log('Screenshot captured:', screenshot.substring(0, 50));
 * } catch (error) {
 *   console.error('Capture failed:', error);
 * }
 * ```
 */
export async function captureViewport(): Promise<string> {
  console.log('[Screenshot] Capturing viewport...');

  // Track viewport dimensions for error reporting
  let viewportWidth = 0;
  let viewportHeight = 0;

  try {
    // Check for browser support
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      throw new Error('Screenshot capture is only available in browser environments');
    }

    // Wait for fonts to load if available (prevents font loading issues)
    if ('fonts' in document) {
      try {
        await (document as any).fonts.ready;
      } catch (e) {
        // Ignore font loading errors
      }
    }

    // First, inject oklch fix into the original document BEFORE html2canvas clones it
    preProcessOklchInDocument();

    // Get VISIBLE viewport dimensions only (not full page)
    viewportWidth = window.innerWidth;
    viewportHeight = window.innerHeight;

    // Get current scroll position to capture the visible area
    const scrollX = window.scrollX || window.pageXOffset || 0;
    const scrollY = window.scrollY || window.pageYOffset || 0;

    // Capture the full document first, then crop to visible area
    const fullWidth = Math.max(document.documentElement.scrollWidth, viewportWidth);
    const fullHeight = Math.max(document.documentElement.scrollHeight, viewportHeight);

    // Get current page origin for CORS filtering
    const currentOrigin = window.location.origin;

    /**
     * Filter function to skip cross-origin images that would cause CORS errors
     * Also skips iframes and other problematic elements
     */
    const filterCrossOrigin = (node: HTMLElement): boolean => {
      // Skip iframes entirely (security risk and often cross-origin)
      if (node.tagName === 'IFRAME') {
        console.log('[Screenshot] Skipping iframe element');
        return false;
      }

      // Check images for cross-origin sources
      if (node.tagName === 'IMG') {
        const img = node as HTMLImageElement;
        const src = img.src || img.getAttribute('src') || '';

        // Skip if no source
        if (!src) return true;

        // Allow data URLs (inline images)
        if (src.startsWith('data:')) {
          return true;
        }

        // Allow blob URLs (local file previews like attachment thumbnails)
        // These are created via URL.createObjectURL() and are valid only in current session
        if (src.startsWith('blob:')) {
          console.log('[Screenshot] Including blob URL image (local preview)');
          return true;
        }

        // Allow same-origin images
        try {
          const imgUrl = new URL(src, currentOrigin);
          if (imgUrl.origin === currentOrigin) {
            return true;
          }

          // Cross-origin image - skip it to avoid CORS error
          console.log(`[Screenshot] Skipping cross-origin image: ${imgUrl.origin}`);
          return false;
        } catch {
          // If URL parsing fails, skip the image
          return false;
        }
      }

      // Check background images on any element
      const style = node.style;
      if (style && style.backgroundImage) {
        const bgImage = style.backgroundImage;
        if (bgImage.includes('url(')) {
          const urlMatch = bgImage.match(/url\(['"]?([^'")\s]+)['"]?\)/);
          if (urlMatch && urlMatch[1]) {
            const bgUrl = urlMatch[1];
            if (!bgUrl.startsWith('data:') && !bgUrl.startsWith('blob:')) {
              try {
                const parsedUrl = new URL(bgUrl, currentOrigin);
                if (parsedUrl.origin !== currentOrigin) {
                  console.log(`[Screenshot] Skipping element with cross-origin background: ${parsedUrl.origin}`);
                  return false;
                }
              } catch {
                // If URL parsing fails, skip element
                return false;
              }
            }
          }
        }
      }

      return true;
    };

    // Transparent 1x1 PNG as placeholder for failed images (404, CORS errors, etc.)
    // This prevents screenshot capture from failing when some images can't be loaded
    const TRANSPARENT_PLACEHOLDER =
      'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=';

    // Capture with html-to-image
    const fullCanvas = await toCanvas(document.body, {
      // Full page dimensions for accurate rendering
      width: fullWidth,
      height: fullHeight,

      // Pixel ratio (cap at 2x to avoid memory issues)
      pixelRatio: Math.min(window.devicePixelRatio || 1, 2),

      // Background color for transparent areas
      backgroundColor: '#ffffff',

      // IMPORTANT: Do NOT use cacheBust: true
      // html-to-image adds timestamp query params to ALL URLs including blob: URLs
      // Blob URLs with query params become invalid (net::ERR_FILE_NOT_FOUND)
      // This breaks screenshot capture when attachment previews are visible
      cacheBust: false,

      // Placeholder for images that fail to load (404, CORS, etc.)
      // Without this, a single broken image will cause the entire screenshot to fail
      imagePlaceholder: TRANSPARENT_PLACEHOLDER,

      // Skip cross-origin images to avoid CORS errors
      filter: filterCrossOrigin,

      // Don't fetch external images that might fail CORS
      skipAutoScale: true,

      // Fetch options for images
      fetchRequestInit: {
        mode: 'cors',
        credentials: 'omit',
      },
    });

    // Crop to visible viewport area
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    const croppedCanvas = document.createElement('canvas');
    croppedCanvas.width = viewportWidth * pixelRatio;
    croppedCanvas.height = viewportHeight * pixelRatio;

    const ctx = croppedCanvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get 2D context for cropping');
    }

    // Draw the visible portion from the full canvas
    ctx.drawImage(
      fullCanvas,
      scrollX * pixelRatio,  // Source X (scroll position)
      scrollY * pixelRatio,  // Source Y (scroll position)
      viewportWidth * pixelRatio,   // Source width
      viewportHeight * pixelRatio,  // Source height
      0,                     // Destination X
      0,                     // Destination Y
      viewportWidth * pixelRatio,   // Destination width
      viewportHeight * pixelRatio   // Destination height
    );

    // Convert to base64 PNG
    const base64 = croppedCanvas.toDataURL('image/png');

    if (!base64 || !base64.startsWith('data:image/')) {
      throw new Error('Failed to generate valid base64 image');
    }

    console.log(`[Screenshot] Captured ${viewportWidth}x${viewportHeight}`);
    return base64;
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error);
    const errorStack = error instanceof Error ? error.stack : 'No stack available';

    console.error('[Screenshot] ❌ CAPTURE FAILED');
    console.error('[Screenshot] Error message:', errorMsg);
    console.error('[Screenshot] Error type:', error?.constructor?.name || 'Unknown');
    console.error('[Screenshot] Full stack:', errorStack);

    // Detailed error diagnostics
    if (errorMsg.includes('unsupported color function')) {
      const colorMatch = errorMsg.match(/unsupported color function ["\']([^"\']+)["\']/);
      if (colorMatch) {
        console.error(`[Screenshot] ⚠️ CRITICAL: html-to-image encountered unsupported color: ${colorMatch[1]}`);
        console.error('[Screenshot] This means the color replacement failed. Possible causes:');
        console.error('  1. Color function in loaded stylesheets from CORS (cannot modify)');
        console.error('  2. Color function in <link> stylesheets that html-to-image is parsing');
        console.error('  3. CSS-in-JS generated styles at render time');
        console.error('  4. SVG or canvas elements with runtime colors');
      }
    }

    if (errorMsg.includes('timeout')) {
      console.error('[Screenshot] ⚠️ Capture timed out - page may be too large or have heavy resources');
      console.error('[Screenshot] Try increasing imageTimeout or reducing page complexity');
    }

    if (errorMsg.includes('Memory') || errorMsg.includes('memory')) {
      console.error('[Screenshot] ⚠️ Out of memory - viewport may be too large');
      console.error(`[Screenshot] Current viewport: ${viewportWidth}x${viewportHeight}`);
      console.error('[Screenshot] Try reducing image scale or splitting capture into smaller areas');
    }

    if (errorMsg.includes('CORS') || errorMsg.includes('cross-origin')) {
      console.error('[Screenshot] ⚠️ CORS error - unable to load cross-origin resources');
      console.error('[Screenshot] This may cause incomplete screenshot capture');
    }

    // Log available diagnostic info
    console.error('[Screenshot] Diagnostic Info:');
    console.error(`  - Device Pixel Ratio: ${window.devicePixelRatio || 1}`);
    console.error(`  - Viewport: ${viewportWidth}x${viewportHeight}`);
    console.error(`  - Inner Window: ${window.innerWidth}x${window.innerHeight}`);
    console.error(`  - Document: ${document.documentElement.scrollWidth}x${document.documentElement.scrollHeight}`);
    console.error(`  - User Agent: ${navigator.userAgent}`);

    throw new Error(
      error instanceof Error
        ? `Screenshot capture failed: ${error.message}`
        : 'Screenshot capture failed due to unknown error'
    );
  } finally {
    // Clean up the temporary oklch fix stylesheet
    cleanupOklchFix();
  }
}

/**
 * Compress base64 image to WebP format
 *
 * Converts PNG/JPEG base64 images to WebP at specified quality.
 * Uses Canvas API for conversion.
 *
 * @param base64 - Base64-encoded data URL (any image format)
 * @param quality - WebP quality (0.0 to 1.0, default: 0.8 = 80%)
 * @returns Promise resolving to WebP base64-encoded data URL
 * @throws {Error} If browser doesn't support WebP or conversion fails
 *
 * @example
 * ```typescript
 * const pngBase64 = 'data:image/png;base64,...';
 * const webpBase64 = await compressToWebP(pngBase64, 0.8);
 * console.log('Compressed to WebP');
 * ```
 */
export async function compressToWebP(base64: string, quality: number = 0.8): Promise<string> {
  try {
    // Validate input
    if (!base64 || !base64.startsWith('data:image/')) {
      throw new Error('Invalid base64 image format');
    }

    if (quality < 0 || quality > 1) {
      throw new Error('Quality must be between 0.0 and 1.0');
    }

    // Check WebP support
    const canvas = document.createElement('canvas');
    const webpSupport = canvas.toDataURL('image/webp').startsWith('data:image/webp');
    if (!webpSupport) {
      console.warn('[Screenshot] WebP not supported, returning original format');
      return base64;
    }

    // Load image
    const img = new Image();
    const loadPromise = new Promise<HTMLImageElement>((resolve, reject) => {
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load image for compression'));
    });
    img.src = base64;
    await loadPromise;

    // Create canvas with image dimensions
    canvas.width = img.width;
    canvas.height = img.height;

    // Draw image to canvas
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get 2D context for compression');
    }
    ctx.drawImage(img, 0, 0);

    // Convert to WebP
    const webpBase64 = canvas.toDataURL('image/webp', quality);

    if (!webpBase64 || !webpBase64.startsWith('data:image/webp')) {
      throw new Error('Failed to convert to WebP format');
    }

    return webpBase64;
  } catch (error) {
    console.error('[Screenshot] WebP compression failed:', error);
    throw new Error(
      error instanceof Error
        ? `WebP compression failed: ${error.message}`
        : 'WebP compression failed due to unknown error'
    );
  }
}

/**
 * Resize base64 image to fit within maximum dimensions
 *
 * Maintains aspect ratio while ensuring image fits within max width/height.
 * Does not upscale images (withoutEnlargement behavior).
 *
 * @param base64 - Base64-encoded data URL
 * @param maxWidth - Maximum width in pixels (default: 1920)
 * @param maxHeight - Maximum height in pixels (default: 1080)
 * @returns Promise resolving to resized base64-encoded data URL
 * @throws {Error} If resize fails
 *
 * @example
 * ```typescript
 * const largeImage = 'data:image/png;base64,...'; // 4K image
 * const resizedImage = await autoResize(largeImage, 1920, 1080);
 * console.log('Resized to fit 1920x1080');
 * ```
 */
export async function autoResize(
  base64: string,
  maxWidth: number = 1920,
  maxHeight: number = 1080
): Promise<string> {
  try {
    // Validate input
    if (!base64 || !base64.startsWith('data:image/')) {
      throw new Error('Invalid base64 image format');
    }

    if (maxWidth <= 0 || maxHeight <= 0) {
      throw new Error('Max dimensions must be positive numbers');
    }

    // Load image
    const img = new Image();
    const loadPromise = new Promise<HTMLImageElement>((resolve, reject) => {
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load image for resize'));
    });
    img.src = base64;
    await loadPromise;

    // Calculate new dimensions (maintain aspect ratio, don't upscale)
    let newWidth = img.width;
    let newHeight = img.height;

    if (newWidth > maxWidth || newHeight > maxHeight) {
      const aspectRatio = newWidth / newHeight;

      if (newWidth > maxWidth) {
        newWidth = maxWidth;
        newHeight = Math.round(newWidth / aspectRatio);
      }

      if (newHeight > maxHeight) {
        newHeight = maxHeight;
        newWidth = Math.round(newHeight * aspectRatio);
      }
    }

    // If no resize needed, return original
    if (newWidth === img.width && newHeight === img.height) {
      return base64;
    }

    // Create canvas and resize
    const canvas = document.createElement('canvas');
    canvas.width = newWidth;
    canvas.height = newHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get 2D context for resize');
    }

    // Use high-quality image smoothing
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Draw resized image
    ctx.drawImage(img, 0, 0, newWidth, newHeight);

    // Extract format and quality from original
    const formatMatch = base64.match(/^data:image\/(png|jpeg|jpg|webp)/);
    const format = formatMatch ? formatMatch[1] : 'png';
    const mimeType = format === 'jpg' ? 'image/jpeg' : `image/${format}`;

    // Convert back to base64
    const resizedBase64 = canvas.toDataURL(mimeType, 0.92);

    if (!resizedBase64 || !resizedBase64.startsWith('data:image/')) {
      throw new Error('Failed to generate resized image');
    }

    return resizedBase64;
  } catch (error) {
    console.error('[Screenshot] Resize failed:', error);
    throw new Error(
      error instanceof Error
        ? `Image resize failed: ${error.message}`
        : 'Image resize failed due to unknown error'
    );
  }
}

/**
 * Validate that base64 image size is within maximum bytes
 *
 * Checks the byte size of a base64-encoded image.
 * Base64 encoding increases size by ~33%, this function accounts for that.
 *
 * @param base64 - Base64-encoded data URL
 * @param maxSizeBytes - Maximum size in bytes (default: 2MB = 2 * 1024 * 1024)
 * @returns True if size is valid, false if exceeds limit
 *
 * @example
 * ```typescript
 * const screenshot = 'data:image/png;base64,...';
 * const isValid = validateSize(screenshot, 2 * 1024 * 1024); // 2MB limit
 * if (!isValid) {
 *   console.error('Screenshot exceeds 2MB size limit');
 * }
 * ```
 */
export function validateSize(base64: string, maxSizeBytes: number = 2 * 1024 * 1024): boolean {
  try {
    if (!base64 || !base64.startsWith('data:image/')) {
      return false;
    }

    // Extract base64 data (remove data URL prefix)
    const base64Data = base64.split(',')[1];
    if (!base64Data) {
      return false;
    }

    // Calculate byte size
    // Base64 encoding: 3 bytes = 4 characters, so size = (length * 3) / 4
    const sizeBytes = (base64Data.length * 3) / 4;

    // Account for padding characters (=)
    const paddingChars = (base64Data.match(/=/g) || []).length;
    const actualSizeBytes = sizeBytes - paddingChars;

    return actualSizeBytes <= maxSizeBytes;
  } catch (error) {
    console.error('[Screenshot] Size validation failed:', error);
    return false;
  }
}

/**
 * Convert base64 data URL to Blob object
 *
 * Useful for uploading screenshots via FormData or direct blob upload.
 * Preserves MIME type from data URL.
 *
 * @param base64 - Base64-encoded data URL
 * @returns Blob containing the image data
 * @throws {Error} If conversion fails or format is invalid
 *
 * @example
 * ```typescript
 * const screenshot = 'data:image/webp;base64,...';
 * const blob = base64ToBlob(screenshot);
 *
 * const formData = new FormData();
 * formData.append('screenshot', blob, 'screenshot.webp');
 * ```
 */
export function base64ToBlob(base64: string): Blob {
  try {
    // Validate input
    if (!base64 || !base64.startsWith('data:')) {
      throw new Error('Invalid base64 data URL format');
    }

    // Extract MIME type and base64 data
    const parts = base64.split(',');
    if (parts.length !== 2) {
      throw new Error('Malformed base64 data URL');
    }

    const mimeMatch = parts[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/png';
    const base64Data = parts[1];

    // Decode base64
    const byteString = atob(base64Data);
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const uint8Array = new Uint8Array(arrayBuffer);

    for (let i = 0; i < byteString.length; i++) {
      uint8Array[i] = byteString.charCodeAt(i);
    }

    // Create Blob
    return new Blob([arrayBuffer], { type: mime });
  } catch (error) {
    console.error('[Screenshot] Base64 to Blob conversion failed:', error);
    throw new Error(
      error instanceof Error
        ? `Failed to convert base64 to Blob: ${error.message}`
        : 'Base64 to Blob conversion failed due to unknown error'
    );
  }
}

/**
 * Get human-readable size from base64 image
 *
 * Utility function to display file size to users.
 *
 * @param base64 - Base64-encoded data URL
 * @returns Human-readable size string (e.g., "1.2 MB", "345 KB")
 *
 * @example
 * ```typescript
 * const screenshot = 'data:image/png;base64,...';
 * const sizeStr = getHumanReadableSize(screenshot);
 * console.log(`Screenshot size: ${sizeStr}`); // "1.5 MB"
 * ```
 */
export function getHumanReadableSize(base64: string): string {
  try {
    if (!base64 || !base64.startsWith('data:image/')) {
      return 'Unknown';
    }

    const base64Data = base64.split(',')[1];
    if (!base64Data) {
      return 'Unknown';
    }

    const sizeBytes = (base64Data.length * 3) / 4;
    const paddingChars = (base64Data.match(/=/g) || []).length;
    const actualSizeBytes = sizeBytes - paddingChars;

    // Convert to human-readable format
    if (actualSizeBytes < 1024) {
      return `${Math.round(actualSizeBytes)} B`;
    } else if (actualSizeBytes < 1024 * 1024) {
      return `${(actualSizeBytes / 1024).toFixed(1)} KB`;
    } else {
      return `${(actualSizeBytes / (1024 * 1024)).toFixed(1)} MB`;
    }
  } catch (error) {
    console.error('[Screenshot] Size calculation failed:', error);
    return 'Unknown';
  }
}
